import { useEffect, useRef, useState } from 'react';

export const useThrottledValue = <T>(
    value: T,
    interval = 250,
    edgeOptions = {
        leading: true,
        trailing: true,
    }
) => {
    const [output, setOutput] = useState<T>(value);
    const lastExecutionTimeRef = useRef(0);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingRef = useRef<T | null>(null);
    const { leading, trailing } = edgeOptions;

    useEffect(() => {
        const now = Date.now();
        const elapsed = now - lastExecutionTimeRef.current;

        if (elapsed >= interval) {
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            lastExecutionTimeRef.current = now;
            if (leading) {
                setOutput(value);
            } else if (trailing) {
                pendingRef.current = value;
                if (!timeoutRef.current) {
                    timeoutRef.current = setTimeout(() => {
                        setOutput(pendingRef.current as T);
                        pendingRef.current = null;
                        timeoutRef.current = null;
                        lastExecutionTimeRef.current = Date.now();
                    }, interval);
                }
            }
        } else if (trailing) {
            pendingRef.current = value;
            if (timeoutRef.current === null) {
                timeoutRef.current = setTimeout(() => {
                    if (pendingRef.current !== null) {
                        setOutput(pendingRef.current);
                    }
                    pendingRef.current = null;
                    timeoutRef.current = null;
                    lastExecutionTimeRef.current = Date.now();
                }, interval - elapsed);
            }
        }
    }, [value, interval, leading, trailing]);

    useEffect(
        () => () => {
            if (timeoutRef.current !== null) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        },
        []
    );

    return output;
};
